import { BaseApi } from '../../api/apiBase';

interface FileUploadResult {
  key: string;
}

interface PresignedUploadData {
  url: string;
  fields: Record<string, string>;
  key: string;
  filename: string;
}

class RegistrationFileUploadApi extends BaseApi {
  constructor() {
    super(); // Uses default /api/portal base URL
  }

  /**
   * Get presigned URL for file upload in registration context
   * @param orderToken - The order token
   * @param filename - The name of the file to upload
   * @param contentType - The MIME type of the file
   */
  async getUploadUrl(orderToken: string, filename: string, contentType?: string): Promise<PresignedUploadData> {
    console.log('RegistrationFileUploadApi.getUploadUrl - orderToken:', orderToken, 'filename:', filename, 'contentType:', contentType);
    
    try {
      const result = await this.postSimpleJson<PresignedUploadData>(
        `/orders/${orderToken}/file_uploads`,
        { filename, content_type: contentType },
        { transformResponseKeys: false }  // Preserve snake_case for S3 fields
      );
      console.log('RegistrationFileUploadApi.getUploadUrl - success:', result);
      return result;
    } catch (error) {
      console.error('RegistrationFileUploadApi.getUploadUrl - error:', error);
      throw error;
    }
  }

  /**
   * Upload file to S3 using presigned POST data
   * @param presignedData - The presigned POST data from getUploadUrl
   * @param file - The file to upload
   */
  async uploadFile(presignedData: PresignedUploadData, file: File): Promise<FileUploadResult> {
    const formData = new FormData();
    
    // Add all fields from presigned post in the correct order
    // Important: The order matters for S3 presigned POST
    const orderedFields = [
      'key',
      'Content-Type', 
      'policy',
      'x-amz-algorithm',
      'x-amz-credential',
      'x-amz-date',
      'x-amz-signature',
      'x-amz-meta-original-filename',
      'success_action_status',
      'acl'
    ];
    
    // First add ordered fields
    orderedFields.forEach(fieldName => {
      if (presignedData.fields[fieldName]) {
        formData.append(fieldName, presignedData.fields[fieldName]);
      }
    });
    
    // Add any remaining fields that weren't in the ordered list
    Object.entries(presignedData.fields).forEach(([key, value]) => {
      if (!orderedFields.includes(key)) {
        formData.append(key, value);
      }
    });
    
    // Add the file last (required by S3)
    formData.append('file', file);
    
    console.log('Uploading to S3 with form data prepared');
    
    // Upload directly to S3
    const response = await fetch(presignedData.url, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('S3 upload error:', response.status, errorText);
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    
    // Extract the key from the response
    const responseText = await response.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(responseText, 'text/xml');
    const key = xmlDoc.querySelector('Key')?.textContent;
    
    if (!key) {
      console.error('S3 Response:', responseText);
      throw new Error('Failed to get file key from S3 response');
    }
    
    return { key };
  }
}

export const registrationFileUploadApi = new RegistrationFileUploadApi();